<template>
  <div class="login-page">
    <img class="logo" src="~assets/img/logo/nsw_logo_full_color.png" alt="NSW Government">
    <div class="login-card">
      <div class="login-heading">
        <h1 class="login-title-main">
          NSW Government<br />Graduate Program
        </h1>
        <h2 class="login-title-sub">Graduate PDP Platform</h2>
      </div>

      <h3 class="login-signin-heading">Sign In</h3>

      <div
        class="alert alert-danger"
        v-if="authError"
        v-html="authError"
      ></div>

      <form @submit.prevent>
        <div class="form-group">
          <label class="control-label">Username</label>
          <input
            type="text"
            name="username"
            class="form-control"
            v-model="username"
            v-validate="'required'"
          >

          <help-block field="username"/>
        </div>

        <div class="form-group">
          <label>Password</label>
          <input
            autocomplete="off"
            type="password"
            name="password"
            class="form-control"
            v-model="password"
            v-validate="'required'"
          >

          <help-block field="password"/>
        </div>

        <p class="mt-2">
          <router-link
            :to="{ name: 'forgot-password' }"
            class="forgot-link"
          >
            Forgot password?
          </router-link>
        </p>

        <ladda-button
          type="submit"
          class="btn btn-primary btn-block mb-3"
          :click="handleAuthenticate"
        >
          Sign In
        </ladda-button>

        <button
          type="button"
          class="btn btn-sso btn-block"
        >
          Single sign on (SSO)
        </button>
      </form>
    </div>
  </div>
</template>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  background-color: #C09491;
  background-image: url('~assets/img/login.jpg');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

.logo {
  width: 100px !important;
  position: absolute;
  top: 2rem;
  left: 2rem;
  z-index: 5;
}

.login-heading {
  text-align: center;
  margin-bottom: 2rem;
  max-width: 100%;
  position: relative;
  top: -50px;
}

.login-title-main {
  font-size: 2.4rem;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 1rem;
}

.login-title-sub {
  font-size: 1.6rem;
  font-weight: 600;
}

.login-signin-heading {
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  text-align: left;
}

.form-group {
  width: 100%;
  max-width: 400px;
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 0.4rem;
  color: #003b8e;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
}

.btn-primary {
  color: #fff;
  font-size: 1.2rem;
  font-weight: 600;
  width: 100%;
  max-width: 380px;
  margin-top: 1rem;
}

.btn-sso {
  background-color: #0087b5;
  border-color: #0087b5;
  color: #fff;
  font-weight: 600;
  width: 100%;
  max-width: 380px;
  margin-top: 1rem;
  display: none;
}

.forgot-link {
  display: inline-block;
  margin-top: 0.6rem;
  font-size: 1rem;
  font-weight: 600;
  color: #003b8e !important;
}

@media (max-width: 900px) {
  .logo {
    width: 90px !important;
    top: 1rem;
    left: 1rem;
  }
  .login-title-main {
    font-size: 2rem;
  }
  .login-title-sub {
    font-size: 1.3rem;
  }
  .login-heading {
    margin-bottom: 1.5rem;
  }
  .form-group {
    max-width: 340px;
  }
  .btn-primary,
  .btn-sso {
    max-width: 340px;
  }
}

@media (max-width: 600px) {
  .login-page {
    padding: 1rem;
    background-size: 120% auto !important;
    background-position: centre center;
  }
  .logo {
    width: 70px !important;
  }
  .login-title-main {
    font-size: 1.7rem;
  }
  .login-title-sub {
    font-size: 1.1rem;
  }
  .login-signin-heading {
    font-size: 1.2rem;
  }
  .form-group {
    max-width: 300px;
  }
  .btn-primary,
  .btn-sso {
    max-width: 300px;
  }
}

@media (max-width: 400px) {
  .login-page {
    padding: 1rem;
    background-size: 150% auto !important;
    background-position: center;
  }
  .logo {
    width: 60px !important;
  }
  .login-title-main {
    font-size: 1.5rem;
  }
  .login-title-sub {
    font-size: 1rem;
  }
  .form-group {
    max-width: 270px;
  }
  .btn-primary,
  .btn-sso {
    max-width: 270px;
  }
}
</style>

<script>
import { authMethods } from 'vx/helpers'
import { handleServerErrors } from 'utils'

export default {
  data () {
    return {
      username: process.env.VUE_APP_LOGIN_USERNAME ? process.env.VUE_APP_LOGIN_USERNAME : null,
      password: process.env.VUE_APP_LOGIN_PASSWORD ? process.env.VUE_APP_LOGIN_PASSWORD : null,
      authError: null
    }
  },

  methods: {
    ...authMethods,

    async handleAuthenticate () {
      this.authError = null

      if (!await this.$validator.validate()) {
        return
      }

      try {
        await this.authenticate({
          username: this.username,
          password: this.password
        })

        await this.authorize()

        if (this.$route.query.redirect) {
          this.$router.push(this.$route.query.redirect)
        } else {
          this.$router.push({ name: 'dashboard' })
        }
      } catch (error) {
        if (error.response.status === 422) {
          handleServerErrors(error, this.$validator)
        } else if (error.response.status === 401) {
          switch (error.response.data.message) {
            case 'account_disabled':
              this.authError = '<p>Your account has been disabled.</p><p>For assistance, please contact the NSW Government Graduate Program team atnswgraduateprogram@premiersdepartment.nsw.gov.au.</p>'
              break
            case 'no_coordinator_agencies':
              this.authError = '<p>You do not have any active agencies.</p><p>For assistance, please contact the NSW Government Graduate Program team at nswgraduateprogram@premiersdepartment.nsw.gov.au.</p>'
              break
            case 'unauthenticated':
            default:
              this.authError = 'Username and/or password is incorrect.'
          }
        }
      }
    }
  }
}
</script>
